import {
  deleteQueryKeys,
  readOptionalBooleanParam,
  readOptionalDateParam,
  readTrimmedParam,
  writeOptionalBooleanParam,
  writeTrimmedParam,
} from "@/lib/filterUrlParams";
import type { LeadFilter } from "@/types/entities";

/**
 * Query keys that round-trip through the Leads URL. Scope (legalEntityIds /
 * branchIds) lives in the settings store, not here — Clear filters still resets
 * it, but it is not written as query params.
 */
export const LEAD_FILTER_PARAM_KEYS = [
  "search",
  "workflowState",
  "startDate",
  "endDate",
  "updatedStartDate",
  "updatedEndDate",
  "reachoutTriggered",
  "reachoutError",
  "crmContactId",
  "atlasContactId",
  "crmSyncStatus",
  "enquiryType",
] as const;

export type LeadFilterParamKey = (typeof LEAD_FILTER_PARAM_KEYS)[number];

/** Patch that `setFilter` treats as "drop every URL-synced key". */
export const CLEARED_LEAD_FILTER: Partial<LeadFilter> = {
  search: undefined,
  workflowState: undefined,
  startDate: undefined,
  endDate: undefined,
  updatedStartDate: undefined,
  updatedEndDate: undefined,
  reachoutTriggered: undefined,
  reachoutError: undefined,
  crmContactId: undefined,
  atlasContactId: undefined,
  crmSyncStatus: undefined,
  enquiryType: undefined,
};

/** Read the Leads list filters out of the current query string. */
export function parseLeadFilterFromSearchParams(params: URLSearchParams): LeadFilter {
  const filter: LeadFilter = {};
  const search = readTrimmedParam(params, "search");
  if (search) filter.search = search;
  const workflowState = readTrimmedParam(params, "workflowState");
  if (workflowState) filter.workflowState = workflowState;
  const startDate = readOptionalDateParam(params, "startDate");
  if (startDate) filter.startDate = startDate;
  const endDate = readOptionalDateParam(params, "endDate");
  if (endDate) filter.endDate = endDate;
  const updatedStartDate = readOptionalDateParam(params, "updatedStartDate");
  if (updatedStartDate) filter.updatedStartDate = updatedStartDate;
  const updatedEndDate = readOptionalDateParam(params, "updatedEndDate");
  if (updatedEndDate) filter.updatedEndDate = updatedEndDate;
  const reachoutTriggered = readOptionalBooleanParam(params, "reachoutTriggered");
  if (reachoutTriggered !== undefined) filter.reachoutTriggered = reachoutTriggered;
  const reachoutError = readTrimmedParam(params, "reachoutError");
  if (reachoutError) filter.reachoutError = reachoutError;
  const crmContactId = readTrimmedParam(params, "crmContactId");
  if (crmContactId) filter.crmContactId = crmContactId;
  const atlasContactId = readTrimmedParam(params, "atlasContactId");
  if (atlasContactId) filter.atlasContactId = atlasContactId;
  const crmSyncStatus = readTrimmedParam(params, "crmSyncStatus");
  if (crmSyncStatus) filter.crmSyncStatus = crmSyncStatus;
  const enquiryType = readTrimmedParam(params, "enquiryType");
  if (enquiryType) filter.enquiryType = enquiryType;
  return filter;
}

/**
 * Overlay the current list filters onto `current`, replacing only the lead-filter
 * keys so unrelated params (if any) survive. Empty values are omitted so Clear
 * filters leaves a clean `/leads` URL.
 */
export function applyLeadFilterToSearchParams(
  filter: LeadFilter,
  current: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(current);
  deleteQueryKeys(next, LEAD_FILTER_PARAM_KEYS);
  writeTrimmedParam(next, "search", filter.search);
  writeTrimmedParam(next, "workflowState", filter.workflowState);
  writeTrimmedParam(next, "startDate", filter.startDate);
  writeTrimmedParam(next, "endDate", filter.endDate);
  writeTrimmedParam(next, "updatedStartDate", filter.updatedStartDate);
  writeTrimmedParam(next, "updatedEndDate", filter.updatedEndDate);
  writeOptionalBooleanParam(next, "reachoutTriggered", filter.reachoutTriggered);
  writeTrimmedParam(next, "reachoutError", filter.reachoutError);
  writeTrimmedParam(next, "crmContactId", filter.crmContactId);
  writeTrimmedParam(next, "atlasContactId", filter.atlasContactId);
  writeTrimmedParam(next, "crmSyncStatus", filter.crmSyncStatus);
  writeTrimmedParam(next, "enquiryType", filter.enquiryType);
  return next;
}

function canonicalQuery(filter: LeadFilter): string {
  return applyLeadFilterToSearchParams(filter, new URLSearchParams()).toString();
}

/** True when both filters would produce the same query string. */
export function leadFiltersEquivalent(a: LeadFilter, b: LeadFilter): boolean {
  return canonicalQuery(a) === canonicalQuery(b);
}

export const leadFilterUrlSyncConfig = {
  parseFromSearchParams: parseLeadFilterFromSearchParams,
  applyToSearchParams: applyLeadFilterToSearchParams,
  filtersEquivalent: leadFiltersEquivalent,
  clearedFilter: CLEARED_LEAD_FILTER,
};
